import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import Modal from "../components/Modal";
import { useLocation } from "react-router-dom";

interface SectionWithModalProps {
  id: string;
  title: string;
  initialContent: React.ReactNode;
  expandedContent?: React.ReactNode;
  imageSrc: string;
  imageAlt: string;
  reverse?: boolean; // For reversing image and text order
  isVisible: boolean;
  sectionRef: React.RefObject<HTMLDivElement | null>;
  modalTitle?: string; // Optional different title for modal
}

const SectionWithModal: React.FC<SectionWithModalProps> = ({
  id,
  title,
  initialContent,
  expandedContent,
  imageSrc,
  imageAlt,
  reverse = false,
  isVisible,
  sectionRef,
  modalTitle,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <section id={id} ref={sectionRef} className="px-4 py-16 md:px-8">
      <div className="mx-auto max-w-6xl">
        <div
          className={`flex flex-col gap-8 md:flex-row ${
            reverse ? "md:flex-row-reverse" : ""
          }`}
        >
          <div
            className={`transition-all duration-1000 ease-in-out md:w-1/2 ${
              isVisible
                ? "w-full translate-x-0 opacity-100"
                : "w-0 translate-x-[-100%] opacity-0"
            }`}
          >
            <img
              src={imageSrc}
              alt={imageAlt}
              className="h-auto w-full rounded-lg shadow-md"
            />
          </div>
          <div className="flex flex-col justify-center md:w-1/2">
            <h1 className="mb-4 text-3xl font-bold md:text-4xl">{title}</h1>
            <div className="text-gray-700">
              {initialContent}

              {expandedContent && (
                <button
                  onClick={openModal}
                  className="mt-4 inline-flex items-center rounded-md bg-[#1a4fa3] px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-[#1a4fa3] focus:ring-offset-2"
                >
                  Read More
                  <svg
                    className="ml-2 h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal for expanded content */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={modalTitle || title}
        content={
          <div className="text-gray-700">
            {initialContent}
            {expandedContent}
          </div>
        }
        imageSrc={imageSrc}
        imageAlt={imageAlt}
      />
    </section>
  );
};

interface LeadershipCardProps {
  name: string;
  title: string;
  id: string;
  initialDescription: string;
  fullDescription?: string;
  imageSrc: string;
  imageAlt: string;
  isVisible: boolean;
  cardRef: React.RefObject<HTMLDivElement | null>;
  reverse?: boolean; // For alternating layout
}

const LeadershipCard: React.FC<LeadershipCardProps> = ({
  name,
  title,
  id,
  initialDescription,
  fullDescription,
  imageSrc,
  imageAlt,
  isVisible,
  cardRef,
  reverse = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <>
      <div
        ref={cardRef}
        id={id}
        className="mb-24 overflow-hidden rounded-xl bg-white shadow-xl"
      >
        <div
          className={`flex flex-col ${reverse ? "md:flex-row-reverse" : "md:flex-row"}`}
        >
          <div
            className={`transition-all duration-1000 ease-in-out md:w-2/5 ${
              isVisible
                ? "translate-y-0 opacity-100 md:translate-x-0"
                : "translate-y-16 opacity-0 md:translate-x-[-100%]"
            }`}
          >
            <div className="h-full overflow-hidden">
              <img
                src={imageSrc}
                alt={imageAlt}
                className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
          </div>
          <div
            className={`flex flex-col justify-center bg-white p-8 md:w-3/5 ${
              isVisible
                ? "translate-y-0 opacity-100 delay-300"
                : "translate-y-16 opacity-0"
            }`}
          >
            <h1 className="mb-2 text-3xl font-bold text-gray-900">{name}</h1>
            <span className="mb-4 inline-block py-1 text-sm font-medium">
              {title}
            </span>
            <p className="mb-4 text-lg leading-relaxed text-gray-700">
              {initialDescription}
            </p>

            {fullDescription && (
              <button
                onClick={openModal}
                className="mt-2 inline-flex w-fit items-center rounded-md bg-[#1a4fa3] px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-[#1a4fa3] focus:ring-offset-2"
              >
                {/* Read Full Bio */}
                Read More
                <svg
                  className="ml-2 h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Modal for full biography */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={`${name} - ${title}`}
        content={
          <div className="text-gray-700">
            <p className="mb-4 text-lg leading-relaxed">{initialDescription}</p>
            {fullDescription && (
              <p className="text-lg leading-relaxed">{fullDescription}</p>
            )}
          </div>
        }
        imageSrc={imageSrc}
        imageAlt={imageAlt}
      />
    </>
  );
};

interface TeamMemberCardProps {
  name: string;
  title: string;
  description: string;
  imageSrc?: string;
  isVisible: boolean;
  delay?: string; // For staggered animations
  imageAlt?: string;
}

const TeamMemberCard: React.FC<TeamMemberCardProps> = ({
  name,
  title,
  description,
  imageSrc,
  isVisible,
  delay = "",
  imageAlt,
}) => (
  <div
    className={`group overflow-hidden rounded-xl bg-white shadow-lg transition-all duration-1000 ease-in-out ${
      isVisible
        ? `translate-y-0 opacity-100 ${delay}`
        : "translate-y-16 opacity-0"
    }`}
  >
    <div className="relative overflow-hidden">
      {imageSrc ? (
        <img
          src={imageSrc}
          alt={imageAlt}
          className="h-80 w-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
        />
      ) : (
        <div className="flex h-64 w-full items-center justify-center bg-gray-200">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-20 w-20 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-50"></div>
    </div>
    <div className="p-4 pb-0">
      <h4 className="mb-2 text-xl font-semibold text-gray-900">{name}</h4>
      <span className="mb-4 inline-block py-1 text-sm font-medium">
        {title}
      </span>
      <p className="text-gray-700c mt-4 hidden">{description}</p>
    </div>
  </div>
);

const useVisibility = (ref: React.RefObject<HTMLElement | null>) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const checkVisibility = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        setIsVisible(rect.top < window.innerHeight && rect.bottom >= 0);
      }
    };

    window.addEventListener("scroll", checkVisibility);
    checkVisibility(); // Initial check
    return () => window.removeEventListener("scroll", checkVisibility);
  }, [ref]);

  return isVisible;
};

interface Slide {
  id: number;
  image: string;
  title: string;
}

const slides: Slide[] = [
  {
    id: 1,
    image: "/assets/imgs/About Us/glorius.webp",
    title: "Growing Legacy",
  },
  {
    id: 2,
    image: "/assets/imgs/About Us/glorius2.webp",
    title: "Growing Legacy",
  },
];

const About: React.FC = () => {
  const location = useLocation(); // Get the current location

  const carouselSettings = {
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    fade: true,
    autoplay: true,
    autoplaySpeed: 3000,
  };

  const storyRef = useRef<HTMLDivElement | null>(null);
  const leader1Ref = useRef<HTMLDivElement | null>(null);
  const leader2Ref = useRef<HTMLDivElement | null>(null);
  const leader3Ref = useRef<HTMLDivElement | null>(null);
  const teamRef = useRef<HTMLDivElement | null>(null);
  const missionRef = useRef<HTMLDivElement | null>(null);
  const visionRef = useRef<HTMLDivElement | null>(null);

  const isStoryVisible = useVisibility(storyRef);
  const isLeader1Visible = useVisibility(leader1Ref);
  const isLeader2Visible = useVisibility(leader2Ref);
  const isLeader3Visible = useVisibility(leader3Ref);
  const isTeamVisible = useVisibility(teamRef);
  const isMissionVisible = useVisibility(missionRef);
  const isVisionVisible = useVisibility(visionRef);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(1 - t, 4)),
      smoothWheel: true,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => lenis.destroy();
  }, []);

  useEffect(() => {
    // This will run on component mount and when location changes
    const pathname = location.pathname;

    // Add a small delay to ensure the page has fully loaded
    const scrollTimeout = setTimeout(() => {
      // Map of paths to section IDs
      const pathToSectionMap: Record<string, string> = {
        "/chairman": "chairman",
        "/chairman/": "chairman",
        "/managing-director": "managing-director",
        "/managing-director/": "managing-director",
        "/ceo": "ceo",
        "/ceo/": "ceo",
      };

      // Check if the current path is in our map
      if (pathname in pathToSectionMap) {
        const sectionId = pathToSectionMap[pathname];
        const element = document.getElementById(sectionId);

        if (element) {
          // Get the element's position relative to the document
          const elementRect = element.getBoundingClientRect();
          const absoluteElementTop = elementRect.top + window.pageYOffset;
          // Calculate position with header offset
          const offsetPosition = absoluteElementTop - 120;

          // Scroll to the element with offset
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });

          // console.log(
          //   `Scrolling to ${sectionId} at position ${offsetPosition}`,
          // );
        } else {
          // console.log(`Element with ID ${sectionId} not found`);
        }
      }
    }, 500); // 500ms delay to ensure everything is rendered

    return () => clearTimeout(scrollTimeout);
  }, [location]);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "Gambetta" }}>
      {/* Carousel Section */}
      <section className="relative overflow-hidden" aria-label="About Us Hero">
        <Slider {...carouselSettings}>
          {slides.map((slide) => (
            <div key={slide.id}>
              <img
                src={slide.image}
                alt={slide.title}
                className="h-[75vh] w-full object-cover"
                loading="eager"
              />
            </div>
          ))}
        </Slider>
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 text-center text-white">
          <div>
            <h1
              style={{ fontFamily: "HistoryPro" }}
              className="animate-fade-in mb-4 text-4xl font-bold md:text-5xl"
            >
              Growing Legacies
            </h1>
          </div>
        </div>
      </section>

      <main>
        {/* Our Story Section */}
        <SectionWithModal
          id="story_section"
          title="Our Story"
          initialContent={
            <>
              <p className="mb-4">
                Century Group was established in 2008 by our esteemed Hon'ble
                chairman, Mr. Sandesh Kumar Jain. Over the years, Century Group
                has evolved and diversified its focus to encompass various
                sectors such as real estate, education, and consultancy.
              </p>
              <p className="mb-4">
                In the realm of real estate, our primary focus lies in creating
                dream homes for our clients. Through meticulous planning and
                execution, we aim to deliver properties that exceed expectations
                and fulfill the aspirations of homeowners. Our commitment to
                excellence has positioned us as a leader in the real estate
                sector in Jabalpur.
              </p>
              <p className="mb-4">
                To further extend our reach and impact, Century Group has
                expanded its operations into other domains. Century Education is
                dedicated to providing top-notch educational services, ensuring
                that individuals have access to quality learning opportunities.
              </p>
            </>
          }
          expandedContent={
            <>
              <p className="mb-4">
                Additionally, Century Developers spearheads the development of
                sites, ensuring that each project is executed with precision and
                meets the highest standards of quality and craftsmanship.
                Through innovative design and meticulous attention to detail,
                Century Developers creates spaces that resonate with both
                functionality and aesthetic appeal.
              </p>
              <p className="mb-4">
                Furthermore, Sanctuary Infratech plays a pivotal role in sales,
                management, and finance within the organization. By leveraging
                expertise in these areas, Sanctuary Infratech optimizes
                processes, facilitates smooth transactions, and ensures sound
                financial management across all ventures undertaken by Century
                Group.
              </p>
              <p className="mb-4">
                As of now, Century Group boasts a workforce of approximately 75
                employees, each contributing their skills and dedication towards
                achieving the company's goals and objectives. With a steadfast
                commitment to excellence and a vision for growth, Century Group
                continues to make significant strides in the realms of real
                estate, education, and consultancy, enriching lives and
                communities along the way.
              </p>
            </>
          }
          imageSrc="/assets/imgs/About Us/aboutcentury.webp"
          imageAlt="Company Journey"
          isVisible={isStoryVisible}
          sectionRef={storyRef}
          modalTitle="Century Group: Our Story"
        />

        {/* Leadership Section with the ID anchors that we'll scroll to */}
        <section className="bg-gradient-to-b from-gray-50 to-white px-4 py-16 md:px-8">
          <div className="mx-auto max-w-6xl">
            <h1 className="mb-16 text-center text-3xl font-bold md:text-5xl">
              Our Leadership
            </h1>

            <LeadershipCard
              name="Mr. Sandesh Kumar Jain"
              title="Chairman"
              id="chairman"
              initialDescription="The chairman's journey from humble beginnings to business success shows his hard work, vision, and determination. Born into a lower-middle-class family, he started his first business—a grain venture—at the age of 14, aiming for self-reliance. As he grew, he expanded into the dal milling industry, and later moved into real estate, where his smart decisions took him to greater heights."
              fullDescription="Guided by the teachings of Shri 108 Achar Shri Vidhya Sagar Ji Mahamuniraj, he focused on honesty and kindness in his business. What started as small real estate projects grew into bigger independent ventures, showing his leadership and commitment to making a positive impact. He also built a school for children, believing in the power of education and community growth. The chairman's journey is a true example of the entrepreneurial spirit—marked by courage, innovation, and a strong sense of social responsibility."
              imageSrc="/assets/imgs/About Us/Chairman_SJ.webp"
              imageAlt="Mr. Sandesh Kumar Jain"
              isVisible={isLeader1Visible}
              cardRef={leader1Ref}
            />

            <LeadershipCard
              name="Mr. Samyak Jain"
              title="Managing Director"
              id="managing-director"
              initialDescription="Samyak Jain is the Managing Director of Century Group, a diversified enterprise headquartered in Jabalpur, Madhya Pradesh, with key operations in real estate, education, and consultancy. A second-generation entrepreneur, Samyak began observing and engaging with the family business at the age of 19, gaining early exposure to the inner workings of the industry. He officially joined the business five years ago, taking on a leadership role with a clear focus on structured growth, modernization, and long-term vision."
              fullDescription="He holds an MBA in Family Business Management from the prestigious SP Jain Institute of Management and Research (SPJIMR), Mumbai. Combining academic insight with practical experience, Samyak has been instrumental in transforming operations, streamlining systems, and expanding Century Group’s footprint in Central India."
              // initialDescription="Mr. Samyak Jain, our Managing Director, exemplifies ambition and entrepreneurial drive. At 19, while pursuing his B.Com at Rani Durgavati University, he joined his family's real estate business. Starting in sales, he quickly gained market insights and took on greater responsibilities, eventually leading the team."
              // fullDescription="Balancing academics and professional growth, he used his education to enhance his business skills. His MBA in Family Business from SP Jain Mumbai further reflects his commitment to continuous learning and development. Mr. Jain's journey is a powerful example of how determination and perseverance drive success, regardless of age or background."
              imageSrc="/assets/imgs/About Us/MD.webp"
              imageAlt="Mr. Samyak Jain"
              isVisible={isLeader2Visible}
              cardRef={leader2Ref}
              reverse
            />

            <LeadershipCard
              name="Dr. Kamesh Satish Pawar"
              title="Vice President for South Asia Management Association"
              id="ceo"
              initialDescription="Dr. Kamesh Satish Pawar, CEO of Century Group Jabalpur, is a forward-thinking leader with expertise in real estate. He holds an MBA in Finance and a Ph.D. in Management, focusing on Blockchain Technology's potential to transform real estate."
              fullDescription="Guided by his mentor, Mr. Sandesh Jain, and the teachings of Shri Acharya Vidysagar Ji, Dr. Pawar emphasizes ethical business practices. His leadership has driven the group's expansion across Jabalpur and beyond, with a focus on innovation, sustainability, and operational excellence. Dr. Pawar aims to position Century Group as a leader in quality, integrity, and customer satisfaction in the real estate sector."
              imageSrc="/assets/imgs/About Us/Kamesh.webp"
              imageAlt="Dr. Kamesh Satish Pawar"
              isVisible={isLeader3Visible}
              cardRef={leader3Ref}
            />

            {/* Team Section */}
            <div ref={teamRef} className="mt-32">
              <h1 className="mb-12 text-center text-3xl font-bold md:text-4xl">
                Our Core Team
              </h1>
              <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                <TeamMemberCard
                  name="Mr. Sant Raj Yadav"
                  title="Project Head - Nakshatra Nagar"
                  description="Mr. Sant Raj Yadav brings extensive expertise to Century Group, contributing to strategic initiatives and operational excellence across projects."
                  imageSrc="/assets/imgs/About Us/yadavji1.webp"
                  isVisible={isTeamVisible}
                />
                <TeamMemberCard
                  name="Ms. Roshni Kundnani"
                  title="Joint Secretary - Sanctuary Education Society & Coordinator - Brain Discovery Global School"
                  description=""
                  imageSrc="/assets/imgs/About Us/Ms. Roshni kundnani.jfif"
                  isVisible={isTeamVisible}
                  delay="delay-200"
                />
                <TeamMemberCard
                  name="Mr. Lavkush Sen"
                  title="Manager - Sanctuary Resort"
                  description=""
                  imageSrc="/assets/imgs/About Us/Mr. Lavkush Sen.jfif"
                  isVisible={isTeamVisible}
                  delay="delay-300"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Our Mission Section */}
        <SectionWithModal
          id="mission"
          title="Our Mission"
          initialContent={
            <ul className="list-disc pl-5 text-gray-700">
              <li className="mb-2">
                Real Estate: Creating sustainable, high-quality developments
                that transform communities.
              </li>
              <li className="mb-2">
                Hospitality: Delivering unmatched comfort and memorable guest
                experiences.
              </li>
              <li className="mb-2">
                Education: Empowering future leaders through innovative and
                holistic learning.
              </li>
              <li className="mb-2">
                Core Values: Upholding integrity, fostering innovation, and
                creating lasting impact.
              </li>
            </ul>
          }
          imageSrc="/assets/imgs/About Us/mission.webp"
          imageAlt="Mission"
          isVisible={isMissionVisible}
          sectionRef={missionRef}
        />

        {/* Our Vision Section */}
        <SectionWithModal
          id="vision"
          title="Our Vision"
          initialContent={
            <ul className="list-disc pl-5 text-gray-700">
              <li className="mb-2">
                Redefining real estate development through iconic projects like
                Nakshatra Nagar and Star Park.
              </li>
              <li className="mb-2">
                Setting new standards of excellence in hospitality with the
                unparalleled Sanctuary Resort.
              </li>
              <li className="mb-2">
                Transforming education through the innovative Brain Discovery
                Global School.
              </li>
              <li className="mb-2">
                Driving meaningful, positive change in every community we serve.
              </li>
            </ul>
          }
          imageSrc="/assets/imgs/About Us/vision.webp"
          imageAlt="Vision"
          reverse
          isVisible={isVisionVisible}
          sectionRef={visionRef}
        />
      </main>
    </div>
  );
};

export default About;
